import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type {
  FontSizeProps,
  FontWeightProps,
  LineHeightProps,
  OverflowProps,
  TextAlignProps,
} from 'styled-system';
import {
  color,
  compose,
  fontSize,
  fontWeight,
  lineHeight,
  overflow,
  space,
  textAlign,
} from 'styled-system';

import type { ColorProps, SpaceProps } from '../theme/system-types';
import type {
  TextDecorationProps,
  TextDecorationThicknessProps,
  TextOverflowProps,
  TextUnderlinePositionProps,
  WhiteSpaceProps,
} from '../theme/system-utilities';
import {
  textDecoration,
  textDecorationThickness,
  textOverflow,
  textUnderlinePosition,
  whiteSpace,
} from '../theme/system-utilities';

/* eslint-disable max-lines */
type BaseTypographyProps = ComponentProps<typeof BaseTypography>;

// Prevent overwriting fontSize and lineHeight in child components
export interface CommonTypographyProps extends Omit<
  BaseTypographyProps,
  keyof FontSizeProps | keyof LineHeightProps
> {
  className?: string;
}

// ⚠️ Do not export this component. Select and use specific Typography component instead.
const BaseTypography = styled.div<
  FontSizeProps &
    LineHeightProps &
    FontWeightProps &
    ColorProps &
    SpaceProps &
    TextAlignProps &
    WhiteSpaceProps &
    OverflowProps &
    TextOverflowProps &
    TextDecorationProps &
    TextUnderlinePositionProps &
    TextDecorationThicknessProps
>(
  compose(
    fontSize,
    lineHeight,
    fontWeight,
    color,
    space,
    textAlign,
    whiteSpace,
    overflow,
    textOverflow
  ),
  textDecoration,
  textUnderlinePosition,
  textDecorationThickness
);

const BaseHeadline = (props: ComponentProps<typeof BaseTypography>) => (
  <BaseTypography m="sp_0" {...props} />
);

// Default colors
const textStrongColor = 'sys.neutral.text.strong';
const textDefaultColor = 'sys.neutral.text.default';

/* =============================================================== */
/* ========================== Headlines ========================== */
const HeadlineH1 = ({ children, ...props }: CommonTypographyProps) => (
  <BaseHeadline
    fontSize={['fs_28', 'fs_32', 'fs_36', 'fs_40']}
    lineHeight={['lh_32', 'lh_36', 'lh_40', 'lh_44']}
    fontWeight="bold"
    color={textStrongColor}
    {...props}
  >
    {children}
  </BaseHeadline>
);

const HeadlineH2 = ({ children, ...props }: CommonTypographyProps) => (
  <BaseHeadline
    fontSize={['fs_24', 'fs_28', 'fs_32', 'fs_36']}
    lineHeight={['lh_28', 'lh_32', 'lh_36', 'lh_40']}
    fontWeight="bold"
    color={textStrongColor}
    {...props}
  >
    {children}
  </BaseHeadline>
);

const HeadlineH3 = ({ children, ...props }: CommonTypographyProps) => (
  <BaseHeadline
    fontSize={['fs_22', 'fs_24', 'fs_28', 'fs_32']}
    lineHeight={['lh_28', null, 'lh_32', 'lh_36']}
    fontWeight="bold"
    color={textStrongColor}
    {...props}
  >
    {children}
  </BaseHeadline>
);

const HeadlineH4 = ({ children, ...props }: CommonTypographyProps) => (
  <BaseHeadline
    fontSize={['fs_20', 'fs_22', 'fs_24', 'fs_28']}
    lineHeight={['lh_24', 'lh_28', null, 'lh_32']}
    fontWeight="bold"
    color={textStrongColor}
    {...props}
  >
    {children}
  </BaseHeadline>
);

const HeadlineH5 = ({ children, ...props }: CommonTypographyProps) => (
  <BaseHeadline
    fontSize={['fs_18', 'fs_20', 'fs_22', 'fs_24']}
    lineHeight={['lh_22', 'lh_24', 'lh_28']}
    fontWeight="bold"
    color={textStrongColor}
    {...props}
  >
    {children}
  </BaseHeadline>
);

const HeadlineH6 = ({ children, ...props }: CommonTypographyProps) => (
  <BaseHeadline
    fontSize={['fs_18', null, null, 'fs_20']}
    lineHeight={['lh_22', null, null, 'lh_24']}
    fontWeight="bold"
    color={textStrongColor}
    {...props}
  >
    {children}
  </BaseHeadline>
);

const HeadlineH7 = ({ children, ...props }: CommonTypographyProps) => (
  <BaseHeadline
    fontSize={['fs_16', 'fs_18', null, 'fs_20']}
    lineHeight={['lh_20', 'lh_22', null, 'lh_24']}
    fontWeight="bold"
    color={textStrongColor}
    {...props}
  >
    {children}
  </BaseHeadline>
);

export const TYPOGRAPHY_H8_FONT_SIZE = ['fs_16', null, null, 'fs_18'];
export const TYPOGRAPHY_H8_LINE_HEIGHT = ['lh_20', null, null, 'lh_22'];

const HeadlineH8 = ({ children, ...props }: CommonTypographyProps) => (
  <BaseHeadline
    fontSize={TYPOGRAPHY_H8_FONT_SIZE}
    lineHeight={TYPOGRAPHY_H8_LINE_HEIGHT}
    fontWeight="bold"
    color={textStrongColor}
    {...props}
  >
    {children}
  </BaseHeadline>
);

const HeadlineH9 = ({ children, ...props }: CommonTypographyProps) => (
  <BaseHeadline
    fontSize={['fs_14', null, null, 'fs_16']}
    lineHeight={['lh_18', null, null, 'lh_20']}
    fontWeight="bold"
    color={textStrongColor}
    {...props}
  >
    {children}
  </BaseHeadline>
);

/* =============================================================== */
/* ========================== Subtitles ========================== */
const SubtitleST1 = ({ children, ...props }: CommonTypographyProps) => (
  <BaseTypography
    fontSize={['fs_20', 'fs_22', 'fs_24', 'fs_28']}
    lineHeight={['lh_24', 'lh_28', null, 'lh_32']}
    color={textStrongColor}
    {...props}
  >
    {children}
  </BaseTypography>
);

const SubtitleST2 = ({ children, ...props }: CommonTypographyProps) => (
  <BaseTypography
    fontSize={['fs_18', 'fs_20', 'fs_22', 'fs_24']}
    lineHeight={['lh_24', null, 'lh_28']}
    color={textStrongColor}
    {...props}
  >
    {children}
  </BaseTypography>
);

const SubtitleST3 = ({ children, ...props }: CommonTypographyProps) => (
  <BaseTypography
    fontSize={['fs_16', null, null, 'fs_18']}
    lineHeight={['lh_20', null, null, 'lh_22']}
    color={textStrongColor}
    {...props}
  >
    {children}
  </BaseTypography>
);

export const TYPOGRAPHY_ST4_FONT_SIZE = ['fs_14', null, null, 'fs_16'];
export const TYPOGRAPHY_ST4_LINE_HEIGHT = ['lh_18', null, null, 'lh_20'];

const SubtitleST4 = ({ children, ...props }: CommonTypographyProps) => (
  <BaseTypography
    fontSize={TYPOGRAPHY_ST4_FONT_SIZE}
    lineHeight={TYPOGRAPHY_ST4_LINE_HEIGHT}
    color={textStrongColor}
    {...props}
  >
    {children}
  </BaseTypography>
);

/* =============================================================== */
/* ============================= Body ============================ */
// Extract specs, so they can be reused for input fields:
const TYPOGRAPHY_BR1_FONT_SIZE = ['fs_16', null, null, 'fs_18'];
const TYPOGRAPHY_BR1_LINE_HEIGHT = ['lh_24', null, null, 'lh_28'];
const TYPOGRAPHY_BR2_FONT_SIZE = ['fs_14', null, null, 'fs_16'];
const TYPOGRAPHY_BR2_LINE_HEIGHT = ['lh_20', null, null, 'lh_24'];

const BodyRegularBR1 = ({ children, ...props }: CommonTypographyProps) => (
  <BaseTypography
    fontSize={TYPOGRAPHY_BR1_FONT_SIZE}
    lineHeight={TYPOGRAPHY_BR1_LINE_HEIGHT}
    color={textDefaultColor}
    {...props}
  >
    {children}
  </BaseTypography>
);

const BodyRegularBR2 = ({ children, ...props }: CommonTypographyProps) => (
  <BaseTypography
    fontSize={TYPOGRAPHY_BR2_FONT_SIZE}
    lineHeight={TYPOGRAPHY_BR2_LINE_HEIGHT}
    color={textDefaultColor}
    {...props}
  >
    {children}
  </BaseTypography>
);

const BodyBoldBB1 = ({ children, ...props }: CommonTypographyProps) => (
  <BodyRegularBR1 fontWeight="bold" {...props}>
    {children}
  </BodyRegularBR1>
);

const BodyBoldBB2 = ({ children, ...props }: CommonTypographyProps) => (
  <BodyRegularBR2 fontWeight="bold" {...props}>
    {children}
  </BodyRegularBR2>
);

const BodyStrikeST1 = styled(BodyRegularBR1)`
  text-decoration: line-through;
`;

const BodyStrikeST2 = styled(BodyRegularBR2)`
  text-decoration: line-through;
`;

/* =============================================================== */
/* ============================ Links ============================ */
const LinkRegularLR1 = ({ children, ...props }: CommonTypographyProps) => (
  <BaseTypography
    fontSize={['fs_16', null, null, 'fs_18']}
    lineHeight={['lh_24', null, null, 'lh_28']}
    color={textStrongColor}
    {...props}
  >
    {children}
  </BaseTypography>
);

const LinkRegularLR2 = ({ children, ...props }: CommonTypographyProps) => (
  <BaseTypography
    fontSize={['fs_14', null, null, 'fs_16']}
    lineHeight={['lh_20', null, null, 'lh_24']}
    color={textStrongColor}
    {...props}
  >
    {children}
  </BaseTypography>
);

const LinkRegularLR3 = ({ children, ...props }: CommonTypographyProps) => (
  <BaseTypography
    fontSize={['fs_12', null, null, 'fs_14']}
    lineHeight={['lh_16', null, null, 'lh_20']}
    color={textStrongColor}
    {...props}
  >
    {children}
  </BaseTypography>
);

const LinkRegularLR4 = ({ children, ...props }: CommonTypographyProps) => (
  <BaseTypography
    fontSize={['fs_10', null, null, 'fs_12']}
    lineHeight={['lh_14', null, null, 'lh_16']}
    color={textStrongColor}
    {...props}
  >
    {children}
  </BaseTypography>
);

const LinkBoldLB1 = ({ children, ...props }: CommonTypographyProps) => (
  <LinkRegularLR1 fontWeight="bold" {...props}>
    {children}
  </LinkRegularLR1>
);

const LinkBoldLB2 = ({ children, ...props }: CommonTypographyProps) => (
  <LinkRegularLR2 fontWeight="bold" {...props}>
    {children}
  </LinkRegularLR2>
);

const LinkBoldLB3 = ({ children, ...props }: CommonTypographyProps) => (
  <LinkRegularLR3 fontWeight="bold" {...props}>
    {children}
  </LinkRegularLR3>
);

const LinkBoldLB4 = ({ children, ...props }: CommonTypographyProps) => (
  <LinkRegularLR4 fontWeight="bold" {...props}>
    {children}
  </LinkRegularLR4>
);

/* =============================================================== */
/* ========================== Captions =========================== */
const CaptionRegularCR1 = ({ children, ...props }: CommonTypographyProps) => (
  <BaseTypography
    fontSize={['fs_12', null, null, 'fs_14']}
    lineHeight={['lh_16', null, null, 'lh_20']}
    color={textDefaultColor}
    {...props}
  >
    {children}
  </BaseTypography>
);

const CaptionRegularCR2 = ({ children, ...props }: CommonTypographyProps) => (
  <BaseTypography
    fontSize={['fs_10', null, null, 'fs_12']}
    lineHeight={['lh_14', null, null, 'lh_16']}
    color={textDefaultColor}
    {...props}
  >
    {children}
  </BaseTypography>
);

const CaptionBoldCB1 = ({ children, ...props }: CommonTypographyProps) => (
  <CaptionRegularCR1 fontWeight="bold" {...props}>
    {children}
  </CaptionRegularCR1>
);

const CaptionBoldCB2 = ({ children, ...props }: CommonTypographyProps) => (
  <CaptionRegularCR2 fontWeight="bold" {...props}>
    {children}
  </CaptionRegularCR2>
);

const CaptionStrikeCS1 = styled(CaptionRegularCR1)`
  text-decoration: line-through;
`;

const CaptionStrikeCS2 = styled(CaptionRegularCR2)`
  text-decoration: line-through;
`;

export {
  HeadlineH1,
  HeadlineH2,
  HeadlineH3,
  HeadlineH4,
  HeadlineH5,
  HeadlineH6,
  HeadlineH7,
  HeadlineH8,
  HeadlineH9,
  SubtitleST1,
  SubtitleST2,
  SubtitleST3,
  SubtitleST4,
  TYPOGRAPHY_BR1_FONT_SIZE,
  TYPOGRAPHY_BR1_LINE_HEIGHT,
  TYPOGRAPHY_BR2_FONT_SIZE,
  TYPOGRAPHY_BR2_LINE_HEIGHT,
  BodyRegularBR1,
  BodyRegularBR2,
  BodyBoldBB1,
  BodyBoldBB2,
  BodyStrikeST1,
  BodyStrikeST2,
  LinkRegularLR1,
  LinkRegularLR2,
  LinkRegularLR3,
  LinkRegularLR4,
  LinkBoldLB1,
  LinkBoldLB2,
  LinkBoldLB3,
  LinkBoldLB4,
  CaptionRegularCR1,
  CaptionRegularCR2,
  CaptionBoldCB1,
  CaptionBoldCB2,
  CaptionStrikeCS1,
  CaptionStrikeCS2,
};
